/**
 * The toast store — a module singleton, deliberately React-free.
 *
 * `notify()` is called from hooks, mutation callbacks, and plain helpers that
 * have no component context, so the queue cannot live in React state. The host
 * (`CgToaster`) subscribes via `useSyncExternalStore`; everything else pushes.
 *
 * Snapshot identity matters: `getSnapshot` must return the *same* array when
 * nothing changed or `useSyncExternalStore` loops forever, so every mutation
 * rebuilds `visible` exactly once and stores it.
 */

/** Max toasts on screen at once; the rest wait in `queue` (T9). */
export const MAX_VISIBLE = 4;

/** Per-variant auto-dismiss, ms. Errors outlive successes on purpose (T8). */
export const DURATIONS = Object.freeze({
  success: 4000,
  info: 6000,
  warning: 8000,
  error: 12000,
});

/**
 * Radix announces `foreground` toasts assertively and interrupts the screen
 * reader; `background` ones wait for a pause. Failures interrupt (T8).
 */
export const ANNOUNCE = Object.freeze({
  success: 'background',
  info: 'background',
  warning: 'foreground',
  error: 'foreground',
});

/** Severity order for the overflow queue — an error waiting behind three
 * successes would be the one thing the user actually needed to see. */
const PRIORITY = { error: 0, warning: 1, info: 2, success: 3 };

let visible = [];
let queue = [];
let listeners = new Set();
let nextId = 0;

const emit = () => {
  for (const listener of listeners) listener();
};

/** Dedupe identity: same severity + same words = the same event (T10). */
const keyOf = (toast) =>
  JSON.stringify([toast.variant, toast.title ?? '', toast.description ?? '']);

/**
 * Pull from `queue` into `visible` while there is room, highest severity
 * first and FIFO within a severity (stable sort on a decorated index).
 */
const drain = () => {
  if (queue.length === 0 || visible.length >= MAX_VISIBLE) return;
  const room = MAX_VISIBLE - visible.length;
  const ordered = queue
    .map((toast, index) => ({ toast, index }))
    .sort((a, b) => PRIORITY[a.toast.variant] - PRIORITY[b.toast.variant] || a.index - b.index);
  const promoted = ordered.slice(0, room).map((entry) => entry.toast);
  const promotedIds = new Set(promoted.map((toast) => toast.id));
  queue = queue.filter((toast) => !promotedIds.has(toast.id));
  visible = [...visible, ...promoted];
};

/**
 * Enqueue a toast. Returns its id — or the id of the live duplicate it folded
 * into, so callers can always dismiss what they just fired.
 *
 * @param {object} input
 * @param {'success'|'error'|'info'|'warning'} input.variant
 * @param {string} [input.title]
 * @param {string} [input.description]
 * @param {string[]} [input.details] extra issue lines behind the expander (T11)
 * @param {number} [input.duration] ms; defaults per variant
 * @param {{label: string, onClick: Function}} [input.action]
 */
export function pushToast({ variant, title, description, details, duration, action }) {
  const candidate = { variant, title, description };
  const key = keyOf(candidate);

  // A repeat while the original is still on screen bumps a counter and
  // restarts the clock instead of stacking (T10) — a failing poll would
  // otherwise bury the screen in identical errors.
  const duplicate = visible.find((toast) => toast.key === key);
  if (duplicate) {
    visible = visible.map((toast) =>
      toast === duplicate
        ? { ...toast, count: toast.count + 1, seq: (nextId += 1) } // new seq resets the Radix timer
        : toast,
    );
    emit();
    return duplicate.id;
  }

  const id = (nextId += 1);
  const toast = {
    id,
    key,
    seq: id,
    variant,
    title,
    description,
    details: details && details.length > 0 ? details : undefined,
    duration: duration ?? DURATIONS[variant],
    action,
    count: 1,
  };

  if (visible.length < MAX_VISIBLE) visible = [...visible, toast];
  else queue = [...queue, toast];

  emit();
  return id;
}

export function updateToast(id, { title, description, details, duration }) {
  const patch = (toast) =>
    toast.id === id
      ? {
          ...toast,
          title,
          description,
          details: details && details.length > 0 ? details : undefined,
          duration: duration ?? toast.duration,
          seq: (nextId += 1),
        }
      : toast;
  if (visible.some((toast) => toast.id === id)) visible = visible.map(patch);
  else if (queue.some((toast) => toast.id === id)) queue = queue.map(patch);
  else return false;
  emit();
  return true;
}

export function dismissToast(id) {
  const before = visible.length + queue.length;
  visible = visible.filter((toast) => toast.id !== id);
  queue = queue.filter((toast) => toast.id !== id);
  if (visible.length + queue.length === before) return;
  drain();
  emit();
}

export function dismissAll() {
  if (visible.length === 0 && queue.length === 0) return;
  visible = [];
  queue = [];
  emit();
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Stable reference between mutations — required by useSyncExternalStore. */
export function getSnapshot() {
  return visible;
}

/** Test-only reset; no production caller. */
export function __resetToastStore() {
  visible = [];
  queue = [];
  listeners = new Set();
  nextId = 0;
}

/** Test-only view of the overflow queue. */
export function __queuedToasts() {
  return queue;
}
